import { FC, useContext, useState } from 'react';
import { CompanyDto } from '../../../dtos';
import { emptyGuid } from '../../../models';
import { useGetBusinessComponentSchedulesQuery } from '../../../store/api/schedule-api';
import { PaginatedProps, useFailedActionSnackbar } from '../../CoreLib/library';
import { ScheduleTableContext } from './ScheduleTableContext';
import { SchedulesTable } from './SchedulesTable';
import { useGetScheduleTableHeaders } from './utils/useGetScheduleTableHeaders';
import { DataFilterContext } from '../../../Contexts';

export interface IBCMsProps {
    company?: CompanyDto;
    studyId?: string;
    searchText?: string;
}

export const BCMs: FC<IBCMsProps> = props => {
    const {
        company,
        studyId,
        searchText: externalSearchText,
    } = props;
    const [searchText, setSearchText] = useState('');
    const [showOnlyExpired, setShowOnlyExpired] = useState(false);
    const [paginatedProps, setPaginatedProps] = useState<PaginatedProps>({
        sortKey: "SCHEDULED",
        sortAsc: false,
        page: 0,
        pageSize: 25,
    });
    const {
        showInactive,
        scheduledFromDateFilter: fromDateFilter,
        scheduledToDateFilter: toDateFilter,
        periodStartDateFilter,
        periodEndDateFilter,
        formTypeFilter,
        reminderFilter,
        studyFilter,
        approvedByFilter,
        statusFilter,
    } = useContext(DataFilterContext);

    const { data, isLoading, isError, refetch } = useGetBusinessComponentSchedulesQuery({
        companyId: company?.id ?? emptyGuid,
        searchText: externalSearchText ?? searchText,
        sortKey: paginatedProps?.sortKey,
        sortAsc: paginatedProps?.sortAsc,
        page: paginatedProps?.page,
        pageSize: paginatedProps?.pageSize,
        includeInactive: showInactive,
        isExpired: showOnlyExpired,
        fromDate: fromDateFilter?.toISOString(),
        toDate: toDateFilter?.toISOString(),
        formType: formTypeFilter ? encodeURIComponent(formTypeFilter) : undefined,
        status: statusFilter ? encodeURIComponent(statusFilter) : undefined,
        reminder: reminderFilter ? encodeURIComponent(reminderFilter) : undefined,
        approvedBy: approvedByFilter ? encodeURIComponent(approvedByFilter) : undefined,
        periodStartDate: periodStartDateFilter?.toISOString(),
        periodEndDate: periodEndDateFilter?.toISOString(),
        assignedToStudyId: studyId ?? studyFilter,
    });

    useFailedActionSnackbar('retrieve', 'company schedules', isError);

    const { tableHeaders } = useGetScheduleTableHeaders({
        companyId: company?.id,
        hideCompanyName: !!company,
        hideReminders: !!studyId,
        hideApprovedBy: !!studyId,
        hideFormType: true,
        hideScheduleDetailStatusCounts: true,
    });

    return (
        <ScheduleTableContext.Provider value={{
            searchText,
            setSearchText,
            paginatedProps,
            setPaginatedProps,
            showOnlyExpired,
            setShowOnlyExpired,
            surveyAlias: 'Business Component Matrix',
            surveyAliasShort: 'BCM'
        }}>
            {!isLoading && (
                <SchedulesTable
                    title=''
                    icon={<></>}
                    company={company}
                    schedules={data!}
                    tableColumns={tableHeaders}
                    isLoading={isLoading}
                    refetch={refetch}
                    editRoute='business-component'
                    detailsRoute='business-component'
                    hideSearch
                    hideBorders
                    hideActiveExpiredTabs
                    hidePauseResumeControls
                    hideAddRespondents
                />
            )}
        </ScheduleTableContext.Provider>
    );
}